function isClient() {
  return !!globalThis?.window || !!import.meta?.client;
}

function globalEmit(key: string, emittedValue?: Record<string, any> | string | boolean | number | undefined) {
  if (!isClient()) return false;
  (globalThis as any)?.__EMITTER__?.emit(key, emittedValue);
}

function globalListener(key: string, listener: (param: any) => void) {
  if (!isClient()) return false;
  (globalThis as any)?.__EMITTER__?.on(key, listener);
}

function globalRemoveListener(key: string, listener: (param: any) => void) {
  if (!isClient()) return false;
  (globalThis as any)?.__EMITTER__?.off(key, listener);
}

function toFirstUpperCase(str: string) {
  if (!str) return '';
  const firstLetter = str.substring(0, 1);
  const remains = str.toLowerCase().substring(1, str.length);
  return `${firstLetter.toUpperCase()}${remains}`;
}

function getAssetFromCdn(path = '') {
  const cdnBase = ((globalThis as any)?.__CDN_URL || '').replace(/\/$/, '');
  const assetPath = path.replace(/^\//, '');
  return [cdnBase, assetPath].join('/');
}

function debounce(func: any, waitFor = 300) {
  let timeoutId: ReturnType<typeof setTimeout>;
  return function (this: any, ...args: any[]) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(this, args), waitFor);
  };
}

function plainChars(str: string) {
  const mask = {
    Ü: 'U',
    ü: 'u',
    Ö: 'O',
    ö: 'o',
    İ: 'i',
    I: 'i',
    ı: 'i',
    Ş: 'S',
    ş: 's',
    Ğ: 'G',
    ğ: 'g',
    Ç: 'C',
    ç: 'c'
  };

  for (const [char, replaceChar] of Object.entries(mask)) {
    str = str.replace(new RegExp(char, 'g'), replaceChar);
  }

  return str;
}

function hexToRgb(hex: string) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1]!, 16),
        g: parseInt(result[2]!, 16),
        b: parseInt(result[3]!, 16)
      }
    : null;
}

function hexToRgba(colorHex:string, opacityPercent = 100) {
  const colorRGB = hexToRgb(colorHex || '');
  return colorRGB ? `rgba(${colorRGB?.r},${colorRGB?.g},${colorRGB?.b}, ${opacityPercent}%)` : `rgba(0,0,0,0)`
}

function getClientNumber(value: number, countryCode: string) {
  return new Intl.NumberFormat(countryCode, { style: 'decimal' }).format(value);
}
function getClientNumberFormat(countryCode: string) {
  const value = getClientNumber(123456.789, countryCode).split('');
  const thousandsSeparator = value.find((val) => isNaN(+val)) || '.';
  const decimalSeperator = value.reverse().find((val) => isNaN(+val)) || ',';
  const options = new Intl.NumberFormat(countryCode, { style: 'decimal' }).resolvedOptions();
  return {
    scale: options.maximumFractionDigits,
    radix: decimalSeperator,
    thousandsSeparator,
    mapToRadix: [decimalSeperator],
    normalizeZeros: true
  }
}
function getRealNumber(value: string | number, countryCode: string, forceDefaultValue = true) {
  const format = getClientNumberFormat(countryCode)
  const thousandsSeparator = new RegExp(`\\${format.thousandsSeparator}`, 'g');
  const decimalSeperator = new RegExp(`\\${format.radix}`, 'g');
  const newValue = `${value}`.replace(thousandsSeparator, '').replace(decimalSeperator, '.') || (forceDefaultValue ? '0' : '')
  // Be sure this is a valid number
  return newValue === '' ? newValue : parseFloat(`${newValue}`);
}

function isJSON(text: string) {
  try {
    JSON.parse(text);
    return true;
  } catch {
    return false;
  }
}


const youtubePattern = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/(watch\?v=|embed\/|shorts\/|video\/)([a-zA-Z0-9_-]+)/;

const vimeoPattern = /^(https?:\/\/)?(www\.)?(vimeo\.com|player\.vimeo\.com)\/(video\/|)([a-zA-Z0-9_-]+)/;

function isYouTubeUrl(url: string) {
  return youtubePattern.test(url)
}

function isVimeoUrl(url: string) {
  return vimeoPattern.test(url)
}

function copyToClipboard(text: string) {
  globalThis?.navigator?.clipboard?.writeText(text);
}

function fetchImage(url: string) {
  const img = new Image();
  img.src = url;
}

function tryToLoadImage(payload: {
  el: HTMLImageElement | HTMLVideoElement,
  srcAttrName?: 'src' | 'poster',
  src?: string;
  timeout?: number;
}) {
  const ts = Date.now();
  const img = new Image();
  const sourceUrl = payload.src || payload.el?.src || '';
  const srcAttrName = payload.srcAttrName || 'src';

  if (!payload.el || !sourceUrl) return false;

  img.onerror = function() {
    if (payload.timeout || (Date.now() - ts < 10000)) {
      setTimeout(function() { img.src = sourceUrl; }, payload.timeout || 1000);
    }
  }
  img.onload = function() {
    if (payload.el) payload.el[srcAttrName as 'src'] = sourceUrl;
  }

  img.src = sourceUrl;
}

function isPlainObject(item: unknown) {
  return (!!item && typeof item === 'object' && !Array.isArray(item));
}

function mergeDeep(target: Record<string, any>, ...sources: Record<string, any>[]) {
  if (!sources.length) return { ...target };
  const source = sources.shift();
  const result = { ...target };

  if (isPlainObject(result) && isPlainObject(source)) {
    for (const key in source) {
      if (isPlainObject(source[key])) {
        result[key] = mergeDeep(result[key] || {}, source[key]);
      } else {
        result[key] = source[key];
      }
    }
  }

  return mergeDeep(result, ...sources);
}

function isURL(str: string) {
  try {
    new URL(str);
    return true;
  } catch {
    return false;
  }
}

export {
  youtubePattern,
  vimeoPattern,
  toFirstUpperCase,
  globalEmit,
  globalListener,
  globalRemoveListener,
  getAssetFromCdn,
  debounce,
  plainChars,
  hexToRgb,
  hexToRgba,
  getClientNumber,
  getClientNumberFormat,
  getRealNumber,
  isJSON,
  isYouTubeUrl,
  isVimeoUrl,
  fetchImage,
  tryToLoadImage,
  copyToClipboard,
  isClient,
  mergeDeep,
  isURL
};
